import React, { useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Form, Input, Row, Col, Typography, Avatar, Button, DatePicker, Switch, AutoComplete } from "antd";
import { DeleteOutlined, UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, PlusOutlined } from "@ant-design/icons";
import { Patient, PatientEthnicity, PatientAdditionalEmail } from "../../../../types/healthcare/PatientTypes";
import SectionCard from "../../../../components/shared/SectionCard";
import PatientNamesSection from "../patientNames/PatientNamesSection";
import PatientPrimaryNameSection, { PatientPrimaryNameEditControls } from "../patientNames/PatientPrimaryNameSection";
import { patientCreateUsername } from "../patientNames/patientNameDisplay";

const { Text } = Typography;

interface ProfileTabProps {
	updatedPatient: Patient;
	setUpdatedPatient: React.Dispatch<React.SetStateAction<Patient>>;
	handleAutoSave: () => void;
	isAnon: boolean;
	/**
	 * B1.4-E: render the structured patient-name section. Opt-in so only the
	 * Personal Info composition loads the names projection; other hosts of this
	 * tab keep the explicit role labels without an extra request.
	 */
	showPatientNames?: boolean;
	/** B1.4-E: parent signal that a canonical primary-name save succeeded. */
	nameSaveSignal?: number;
	/**
	 * CU-86d439zzf: the host's explicit primary-name edit session. Supplied only
	 * by a host that owns a canonical commit path; without it the Primary Name
	 * controls keep their existing directly-editable behaviour.
	 */
	primaryNameEdit?: PatientPrimaryNameEditControls;
}

const ProfileTab: React.FC<ProfileTabProps> = ({ updatedPatient, setUpdatedPatient, handleAutoSave, isAnon, showPatientNames, nameSaveSignal, primaryNameEdit }) => {
	const handleChange = (field: string, value: any) => {
		setUpdatedPatient((prev) => ({ ...prev, [field]: value }));
	};

	// Nested setter for patient_info.* fields. Non-text inputs (Switch/Select/
	// DatePicker/array editors) trigger an immediate auto-save since they don't blur.
	const handlePatientInfo = (field: string, value: any, save = true) => {
		setUpdatedPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, [field]: value } }));
		if (save) handleAutoSave();
	};

	/*
	 * PR #199 review correction — the one-name-only save must observe the
	 * COMMITTED state.
	 *
	 * Calling handleAutoSave() in the same event turn as setUpdatedPatient let the
	 * host's save callback close over the pre-toggle patient, so it could submit
	 * the previous has_one_name_only flag and the name components that were just
	 * cleared. The save is therefore deferred to an effect that runs after the new
	 * state has rendered.
	 *
	 * Two refs keep that correct without changing any host's prop contract:
	 *
	 *  - oneNameOnlySavePending marks that a toggle (not a keystroke, not the
	 *    initial render and not an incoming patient load) asked for a save, so
	 *    exactly one save follows exactly one toggle;
	 *  - autoSaveRef holds the CURRENT callback. The effect deliberately does not
	 *    depend on handleAutoSave: the Personal Info host recreates a debounced
	 *    callback on every render, and depending on its identity would re-run the
	 *    effect and save repeatedly. Reading it from the ref still invokes the
	 *    freshest callback, which is the one closing over the committed state.
	 */
	const oneNameOnlySavePending = useRef(false);
	const autoSaveRef = useRef(handleAutoSave);

	useEffect(() => {
		autoSaveRef.current = handleAutoSave;
	}, [handleAutoSave]);

	useEffect(() => {
		if (!oneNameOnlySavePending.current) return;
		oneNameOnlySavePending.current = false;
		autoSaveRef.current();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatedPatient]);

	const handleOneNameOnly = (checked: boolean) => {
		setUpdatedPatient((prev) => ({
			...prev,
			has_one_name_only: checked,
			...(checked ? { first_name: "", middle_name: "" } : {}),
		}));
		oneNameOnlySavePending.current = true;
	};

	const ethnicities: PatientEthnicity[] = updatedPatient.patient_info?.ethnicities || [];
	const additionalEmails: PatientAdditionalEmail[] = updatedPatient.patient_info?.additional_emails || [];

	const updateEthnicity = (index: number, label: string) => {
		const next = ethnicities.map((e, i) => (i === index ? { ...e, label } : e));
		handlePatientInfo("ethnicities", next, false);
	};
	const addEthnicity = () => handlePatientInfo("ethnicities", [...ethnicities, { label: "" }], false);
	const removeEthnicity = (index: number) => handlePatientInfo("ethnicities", ethnicities.filter((_, i) => i !== index));

	const updateEmail = (index: number, key: "type" | "email", value: string) => {
		const next = additionalEmails.map((e, i) => (i === index ? { ...e, [key]: value } : e));
		handlePatientInfo("additional_emails", next, false);
	};
	const addEmail = () => handlePatientInfo("additional_emails", [...additionalEmails, { type: "Home", email: "" }], false);
	const removeEmail = (index: number) => handlePatientInfo("additional_emails", additionalEmails.filter((_, i) => i !== index));

	const currentProfilePicture = updatedPatient.profile_picture || "/assets/images/default-profile.png";

	if (isAnon) {
		return (
			<Form layout="vertical">
				<Row gutter={[16, 0]}>
					<Col xs={24} md={12}>
						<Form.Item label="Email">
							<Input type="email" value={updatedPatient.email || ""} onChange={(e) => handleChange("email", e.target.value)} onBlur={handleAutoSave} placeholder="Enter email" prefix={<MailOutlined />} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Username">
							<Input value={updatedPatient.username || ""} onChange={(e) => handleChange("username", e.target.value)} onBlur={handleAutoSave} placeholder="Enter username" prefix={<UserOutlined />} />
						</Form.Item>
					</Col>
					{updatedPatient.id === "" && (
						<>
							<Col xs={24} md={12}>
								<Form.Item label="Password">
									<Input.Password value={updatedPatient.password || ""} onChange={(e) => handleChange("password", e.target.value)} onBlur={handleAutoSave} placeholder="Enter password" />
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item label="Password Confirmation">
									<Input.Password value={updatedPatient.password_confirmation || ""} onChange={(e) => handleChange("password_confirmation", e.target.value)} onBlur={handleAutoSave} placeholder="Confirm password" />
								</Form.Item>
							</Col>
						</>
					)}
				</Row>
			</Form>
		);
	}

	const fi = { marginBottom: 8 };

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{/* The picture route is self-only and ignores user_id, so staff get no upload or delete here (14ygtkjcvyx). */}
			<div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)", borderRadius: 8 }}>
				<Avatar size={52} src={currentProfilePicture} alt="Patient avatar" style={{ border: "2px solid var(--mg-border)", flexShrink: 0 }} />
			</div>

			{/*
			 * CU-86d439zzf: the canonical primary name is one explicitly edited
			 * group, kept out of Basic Information so an unrelated demographic
			 * autosave cannot carry a half-edited name tuple.
			 */}
			<PatientPrimaryNameSection
				patient={updatedPatient}
				onFieldChange={handleChange}
				onOneNameOnlyChange={handleOneNameOnly}
				onFieldBlur={handleAutoSave}
				edit={primaryNameEdit}
			/>

			<SectionCard title="Basic Information">
				<Form layout="vertical" size="small">
					<Row gutter={[8, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Username" style={fi}>
								<Input
									/*
									 * P2 (86d3yf9q7): the preview is the bare `first_last` slug that
									 * PatientModal actually submits, from the SAME helper. It used to
									 * append a timestamp the create never sent, so the shown username
									 * was not the one the patient would log in with and a duplicate
									 * name 422'd on a value the operator had never been shown.
									 *
									 * Editable only before the record exists: since the slug is never
									 * auto-suffixed, editing it here is how an operator resolves a
									 * duplicate-name collision without abandoning the form.
									 */
									aria-label="Username"
									disabled={updatedPatient.id !== ""}
									value={updatedPatient.id !== "" ? updatedPatient.username || "" : patientCreateUsername(updatedPatient)}
									onChange={(e) => handleChange("username", e.target.value)}
									prefix={<UserOutlined />}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Gender" style={fi}>
								<Input value={updatedPatient.gender || ""} onChange={(e) => handleChange("gender", e.target.value)} onBlur={handleAutoSave} placeholder="Gender" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Sex" style={fi}>
								<Input value={updatedPatient.sex || ""} onChange={(e) => handleChange("sex", e.target.value)} onBlur={handleAutoSave} placeholder="Male / Female" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Date of Birth" style={fi}>
								<DatePicker
									value={updatedPatient.date_of_birth ? dayjs(String(updatedPatient.date_of_birth).slice(0, 10)) : null}
									format="DD/MM/YYYY"
									placeholder="DD/MM/YYYY"
									inputReadOnly
									disabledDate={(d) => d && d.isAfter(dayjs())}
									onKeyDown={(e) => e.preventDefault()}
									onPaste={(e) => e.preventDefault()}
									onChange={(date) => handleChange("date_of_birth", date ? date.format("YYYY-MM-DD") : null)}
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Height" style={fi}>
								<Input value={updatedPatient.height || ""} onChange={(e) => handleChange("height", e.target.value)} onBlur={handleAutoSave} placeholder="Height" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Weight" style={fi}>
								<Input value={updatedPatient.weight || ""} onChange={(e) => handleChange("weight", e.target.value)} onBlur={handleAutoSave} placeholder="Weight" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Pronouns" style={fi}>
								<AutoComplete
									style={{ width: "100%" }}
									value={updatedPatient.patient_info?.pronouns || ""}
									options={[{ value: "he/him" }, { value: "she/her" }, { value: "they/them" }]}
									onChange={(value) => handlePatientInfo("pronouns", value, false)}
									onBlur={handleAutoSave}
									placeholder="e.g. she/her"
									allowClear
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Occupation" style={fi}>
								<Input value={updatedPatient.patient_info?.occupation || ""} onChange={(e) => handlePatientInfo("occupation", e.target.value, false)} onBlur={handleAutoSave} placeholder="Occupation" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Date Deceased" style={fi}>
								<DatePicker
									value={updatedPatient.patient_info?.deceased_date ? dayjs(String(updatedPatient.patient_info.deceased_date).slice(0, 10)) : null}
									format="DD/MM/YYYY"
									placeholder="DD/MM/YYYY"
									inputReadOnly
									disabledDate={(d) => d && d.isAfter(dayjs())}
									onChange={(date) => handlePatientInfo("deceased_date", date ? date.format("YYYY-MM-DD") : null)}
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Transgender" style={fi}>
								<div>
									<Switch checked={!!updatedPatient.patient_info?.is_transgender} onChange={(checked) => handlePatientInfo("is_transgender", checked)} />
								</div>
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Aboriginal / Torres Strait Islander" style={fi}>
								<div>
									<Switch checked={!!updatedPatient.patient_info?.atsi} onChange={(checked) => handlePatientInfo("atsi", checked)} />
								</div>
							</Form.Item>
						</Col>
					</Row>
					{updatedPatient.id === "" && (
						/* P2 (86d3yf9q7): no longer the username — a random password is
						   generated on create and is only ever readable from the welcome
						   notification, so say so rather than leaving the old promise up. */
						<Text type="secondary" style={{ fontSize: 12 }}>A secure password is generated on creation and included in the welcome notification.</Text>
					)}
				</Form>
			</SectionCard>

			{/*
			 * B1.4-E: the structured patient-name section. Rendered only for an
			 * existing patient — the projection is keyed on a real patient id, and a
			 * not-yet-created record has no server-side names to read.
			 */}
			{showPatientNames && !!updatedPatient.id && (
				<PatientNamesSection patientId={updatedPatient.id} primaryNameSaveSignal={nameSaveSignal} />
			)}

			<SectionCard title="Contact & Address">
				<Form layout="vertical" size="small">
					<Row gutter={[8, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Email" style={fi}>
								<Input type="email" value={updatedPatient.email || ""} onChange={(e) => handleChange("email", e.target.value)} onBlur={handleAutoSave} placeholder="Email" prefix={<MailOutlined />} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Phone" style={fi}>
								<Input value={updatedPatient.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} onBlur={handleAutoSave} placeholder="Phone number" prefix={<PhoneOutlined />} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Address" style={fi}>
								<Input value={updatedPatient.address || ""} onChange={(e) => handleChange("address", e.target.value)} onBlur={handleAutoSave} placeholder="Street address" prefix={<HomeOutlined />} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="City" style={fi}>
								<Input value={updatedPatient.city || ""} onChange={(e) => handleChange("city", e.target.value)} onBlur={handleAutoSave} placeholder="City" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="State" style={fi}>
								<Input value={updatedPatient.state || ""} onChange={(e) => handleChange("state", e.target.value)} onBlur={handleAutoSave} placeholder="State" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Country" style={fi}>
								<Input value={updatedPatient.country || ""} onChange={(e) => handleChange("country", e.target.value)} onBlur={handleAutoSave} placeholder="Country" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Post Code" style={fi}>
								<Input value={updatedPatient.postal_code || ""} onChange={(e) => handleChange("postal_code", e.target.value)} onBlur={handleAutoSave} placeholder="Post code" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Country of Birth" style={fi}>
								<Input value={updatedPatient.patient_info?.country_of_birth || ""} onChange={(e) => handlePatientInfo("country_of_birth", e.target.value, false)} onBlur={handleAutoSave} placeholder="Country of birth" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Interpreter Required" style={fi}>
								<div>
									<Switch checked={!!updatedPatient.patient_info?.interpreter_required} onChange={(checked) => handlePatientInfo("interpreter_required", checked)} />
								</div>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			<SectionCard title="Additional Details">
				<Form layout="vertical" size="small">
					<Text type="secondary" style={{ fontSize: 12 }}>Ethnicities</Text>
					{ethnicities.length === 0 && (
						<div style={{ fontSize: 12, color: "var(--mg-text-secondary)", margin: "4px 0 8px" }}>None recorded.</div>
					)}
					{ethnicities.map((eth, index) => (
						<Row key={index} gutter={[8, 0]} align="middle" style={{ marginBottom: 8 }}>
							<Col flex="auto">
								<Input value={eth.label || ""} onChange={(e) => updateEthnicity(index, e.target.value)} onBlur={handleAutoSave} placeholder="Ethnicity" />
							</Col>
							<Col flex="none">
								<Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeEthnicity(index)} />
							</Col>
						</Row>
					))}
					<Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addEthnicity} style={{ marginBottom: 16 }}>Add Ethnicity</Button>

					<Text type="secondary" style={{ fontSize: 12 }}>Additional Emails</Text>
					{additionalEmails.length === 0 && (
						<div style={{ fontSize: 12, color: "var(--mg-text-secondary)", margin: "4px 0 8px" }}>None recorded.</div>
					)}
					{additionalEmails.map((em, index) => (
						<Row key={index} gutter={[8, 0]} align="middle" style={{ marginBottom: 8 }}>
							<Col xs={8} md={6}>
								<Input value={em.type || ""} onChange={(e) => updateEmail(index, "type", e.target.value)} onBlur={handleAutoSave} placeholder="Type (Home/Work)" />
							</Col>
							<Col flex="auto">
								<Input type="email" value={em.email || ""} onChange={(e) => updateEmail(index, "email", e.target.value)} onBlur={handleAutoSave} placeholder="Email address" prefix={<MailOutlined />} />
							</Col>
							<Col flex="none">
								<Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeEmail(index)} />
							</Col>
						</Row>
					))}
					<Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addEmail}>Add Email</Button>
				</Form>
			</SectionCard>
		</div>
	);
};

export default ProfileTab;
